import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import useApi from "../../../hook/useApi";
import { useNavigate } from "react-router-dom";
import { copyToClipboard, stripHtml, trimAddress } from "../../../utils/helper";
import { IoCopyOutline } from "react-icons/io5";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import Loader from "../../Loader/Loader";

const UnitCount = () => {
  const { AsyncGetApiCall } = useApi();
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const [topSellingNfts, setTopSellingNfts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [buyers, setBuyers] = useState([]);

  const [activePropsals, setActivePropsals] = useState([]);

  const getData = async () => {
    try {
      setLoading(true)
      const response = await AsyncGetApiCall("/admin/dashboard-data");

      if (response?.success) {
        const dashboard = response;

        console.log("response", response)

        const mappedData = {
          users: {
            title: "Users",
            value: dashboard?.usersCount ?? 0,
            route: "/users-list",
          },
          hotel_bookings: {
            title: "NFTs",
            value: dashboard?.nftsCount ?? 0,
            route: "/nft-list",
          },
          flight_bookings: {
            title: "ICOs",
            value: dashboard?.icoCount ?? 0,
            route: "/ico-manage",
          },
          income: {
            title: "Proposals",
            value: dashboard?.propsalCount ?? 0,
            route: "/proposal",
          },
          expense: {
            title: "Collections",
            value: dashboard?.collectionCount ?? 0,
            route: "/nft-collections",
          },
        };

        setData(mappedData);

        setTopSellingNfts(dashboard?.topSellingNfts || []);

        setBuyers(dashboard?.recentlyBuyers || []);

        setActivePropsals(dashboard?.activePropsals || []);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
    finally {
      setLoading(false)
    }
  };

  useEffect(() => {
    getData();
  }, []);

  // Helper function to render arrow and change value
  const renderTrend = (change) => {
    const isPositive = change > 0;
    return (
      <span
        className={`d-inline-flex align-items-center gap-1 ${isPositive ? "text-success-main" : "text-danger-main"
          }`}
      ></span>
    );
  };

  // Define card-specific configurations (icons and background colors)
  const cardConfigs = {
    users: {
      icon: "gridicons:multiple-users",
      bgColor: "bg-cyan",
      gradient: "bg-gradient-start-1",
    },
    hotel_bookings: {
      icon: "fa-solid:award",
      bgColor: "bg-purple",
      gradient: "bg-gradient-start-2",
    },
    flight_bookings: {
      icon: "fluent:people-20-filled",
      bgColor: "bg-info",
      gradient: "bg-gradient-start-3",
    },
    income: {
      icon: "solar:wallet-bold",
      bgColor: "bg-success-main",
      gradient: "bg-gradient-start-4",
    },
    expense: {
      icon: "fa6-solid:file-invoice-dollar",
      bgColor: "bg-red",
      gradient: "bg-gradient-start-5",
    },
  };

  const handleCardClick = (route) => {
    if (route) {
      navigate(route);
    }
  };

  ///pie chart
  const COLORS = ["#00C49F", "#FF8042"];
  const listed = topSellingNfts.filter((n) => n.isMarketplace).length;
  const unlisted = topSellingNfts.length - listed;
  const nftPieData = [
    { name: "Listed", value: listed },
    { name: "Unlisted", value: unlisted },
  ];

  return (
    <>
      {loading && <Loader />}
      <div>
        <div className="row row-cols-xxxl-5 row-cols-lg-3 row-cols-sm-2 row-cols-1 gy-4">
          {data &&
            Object.entries(data).map(([key, metric]) => (
              <div className="col" key={key}>
                <div
                  className={`card shadow-none border ${cardConfigs[key]?.gradient || "bg-gradient-start-1"
                    } h-100 cursor-pointer`} // ✅ Add cursor pointer
                  onClick={() => handleCardClick(metric.route)} // ✅ Make card clickable
                  style={{ transition: "0.2s", cursor: "pointer" }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.transform = "scale(1.03)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "scale(1)")
                  }
                >
                  <div className="card-body p-20">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                      <div>
                        <p className="fw-medium text-primary-light mb-1">
                          {metric.title}
                        </p>
                        <h6 className="mb-0">{metric.value ?? 0}</h6>
                      </div>
                      <div
                        className={`w-50-px h-50-px ${cardConfigs[key]?.bgColor || "bg-cyan"
                          } rounded-circle d-flex justify-content-center align-items-center`}
                      >
                        <Icon
                          icon={
                            cardConfigs[key]?.icon || "gridicons:multiple-users"
                          }
                          className="text-white text-2xl mb-0"
                        />
                      </div>
                    </div>
                    <p className="fw-medium text-sm text-primary-light mt-12 mb-0 d-flex align-items-center gap-2">
                      {renderTrend(metric?.change ?? 0)}
                      <span>{metric.label}</span>
                    </p>
                  </div>
                </div>
              </div>
            ))}
        </div>

        {/* Dashboard Graph Section */}
        <div className="col-xxl-12 d-flex justify-center align-center gap-2 mt-4">
          <div className="col-xxl-6 col-xl-6 mt-8">
            <div className="card h-100">
              <div className="card-body p-24">
                <div className="d-flex flex-wrap align-items-center gap-1 justify-content-between mb-16">
                  <h6>Platform Overview</h6>
                </div>

                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={[
                      { name: "Users", value: data?.users?.value || 0 },
                      { name: "NFTs", value: data?.hotel_bookings?.value || 0 },
                      { name: "ICOs", value: data?.flight_bookings?.value || 0 },
                      { name: "Proposals", value: data?.income?.value || 0 },
                      { name: "Collections", value: data?.expense?.value || 0 },
                    ]}
                    margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#007bff" radius={[10, 10, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="col-xxl-6 col-xl-6 mt-4">
            <div className="card h-100">
              <div className="card-body p-24">
                <h6>NFT Marketplace Distribution</h6>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={nftPieData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label
                    >
                      {nftPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index]} />
                      ))}
                    </Pie>
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        <br />

        {/* Top Selling NFTs Section */}
        <div className="col-xxl-12 col-xl-12 mt-4">
          <div className="card h-100">
            <div className="card-body p-24">
              <div className="d-flex flex-wrap align-items-center gap-1 justify-content-between mb-16">
                <h6>Top Selling NFTs</h6>
              </div>

              <div className="table-responsive scroll-sm">
                <table className="table bordered-table sm-table mb-0">
                  <thead>
                    <tr>
                      <th scope="col">NFT</th>
                      <th scope="col">Title</th>
                      <th scope="col">Token Id</th>
                      <th scope="col">Sell Count</th>
                      <th scope="col">Marketplace</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topSellingNfts && topSellingNfts.length > 0 ? (
                      topSellingNfts.map((nft) => (
                        <tr key={nft?._id}>
                          <td>
                            <div className="d-flex align-items-center">
                              <img
                                src={nft?.image}
                                alt={nft?.title}
                                className="w-40-px h-40-px rounded-circle flex-shrink-0 me-12 overflow-hidden object-cover"
                              />
                              <div className="flex-grow-1">
                                <h6 className="text-md mb-0 fw-medium">
                                  {nft?.title ? (nft.title.length > 10 ? nft.title.slice(0, 10) + '...' : nft.title) : "N/A"}
                                </h6>
                                <span className="text-sm text-secondary-light fw-medium">
                                  {nft?.description ? (nft.description.length > 15 ? nft.description.slice(0, 15) + '...' : nft.description) : "—"}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>{nft?.title || "N/A"}</td>
                          <td>{nft?.tokenId || "N/A"}</td>
                          <td className="text-center fw-semibold">
                            {nft?.sellCount || 0}
                          </td>
                          <td className="text-center">
                            <span
                              className={`px-20 py-4 rounded-pill fw-medium text-sm ${nft?.isMarketplace
                                ? "bg-success-focus text-success-main"
                                : "bg-danger-focus text-danger-main"
                                }`}
                            >
                              {nft?.isMarketplace ? "Listed" : "Not Listed"}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="text-center">
                          No NFTs found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <br />

        {/* Buyers */}
        <div className="col-xxl-12 col-xl-12 mt-4">
          <div className="card h-100">
            <div className="card-body p-24">
              <div className="d-flex flex-wrap align-items-center gap-1 justify-content-between mb-16">
                <h6>Recently Nfts Buyers</h6>
              </div>

              <div className="table-responsive scroll-sm">
                <table className="table bordered-table sm-table mb-0">
                  <thead>
                    <tr>
                      <th scope="col">NFT</th>
                      <th scope="col">Title</th>
                      <th scope="col">Token ID</th>
                      <th scope="col">Buyer Name</th>
                      <th scope="col">Wallet Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {buyers && buyers.length > 0 ? (
                      buyers.map((item, index) => (
                        <tr key={item?.nft?._id || index}>
                          {/* NFT Image */}
                          <td>
                            <div className="d-flex align-items-center">
                              <img
                                src={
                                  item?.nft?.image ||
                                  "/assets/images/placeholder.png"
                                }
                                alt="NFT"
                                className="w-40-px h-40-px rounded-circle flex-shrink-0 me-12 overflow-hidden object-cover"
                              />
                            </div>
                          </td>

                          {/* Token ID */}
                          <td>{item?.nft?.title || "N/A"}</td>

                          {/* Token ID */}
                          <td>{item?.nft?.tokenId || "N/A"}</td>

                          {/* Buyer Name */}
                          <td>{item?.user?.name || "User"}</td>

                          {/* Wallet Address */}
                          <td className="text-sm text-secondary-light">
                            {trimAddress(item?.user?.walletAddress, 6, 6) ||
                              trimAddress(item?.currentOwner, 6, 6) ||
                              "N/A"}
                            {(item?.user?.walletAddress || item?.currentOwner) && (
                              <button
                                onClick={() => copyToClipboard(item?.user?.walletAddress || item?.currentOwner)}
                                className="ms-3 p-0 border-0 bg-transparent"
                                style={{ lineHeight: 1 }}
                              >
                                <IoCopyOutline />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center">
                          No buyers found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <br />

        {/* Active Proposals */}
        <div className="col-xxl-12 col-xl-12 mt-4">
          <div className="card h-100">
            <div className="card-body p-24">
              <div className="d-flex flex-wrap align-items-center gap-1 justify-content-between mb-16">
                <h6>Active Proposals</h6>
              </div>

              <div className="table-responsive scroll-sm">
                <table className="table bordered-table sm-table mb-0">
                  <thead>
                    <tr>
                      <th scope="col">Title</th>
                      <th scope="col">Description</th>
                      <th scope="col">Start Time</th>
                      <th scope="col">End Time</th>
                      <th scope="col">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activePropsals && activePropsals.length > 0 ? (
                      activePropsals.map((proposal, index) => (
                        <tr key={proposal?._id || index}>
                          <td>{proposal?.title || "N/A"}</td>
                          <td
                            className="text-truncate"
                            style={{ maxWidth: "250px" }}
                          >
                            {stripHtml(proposal?.description || "—")}
                          </td>
                          <td>
                            {new Date(
                              proposal?.startTime * 1000
                            ).toLocaleString() || "N/A"}
                          </td>

                          <td>
                            {new Date(
                              proposal?.endTime * 1000
                            ).toLocaleString() || "N/A"}
                          </td>
                          <td>
                            <span className="px-20 py-4 rounded-pill fw-medium text-sm bg-success-focus text-success-main">
                              Active
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="text-center">
                          No active proposals found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UnitCount;
